import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { businessApi, appointmentApi, subscriptionApi } from '../services/api';
import { Card, Row, Col, Button, ListGroup, Alert, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const BusinessDashboard = () => {
  const { user, logout } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [dashboardRes, subscriptionRes, usageRes] = await Promise.all([
          businessApi.getDashboard(),
          subscriptionApi.getCurrent().catch(() => ({ data: { data: { subscription: null, plan: null, isActive: false } } })),
          subscriptionApi.getUsage().catch(() => ({ data: { data: null } }))
        ]);
        setDashboardData(dashboardRes.data.data);
        setSubscription(subscriptionRes.data.data);
        setUsage(usageRes.data.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="business-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Welcome, {user?.business?.name || user?.email}</h2>
        <Link to="/dashboard/subscription">
          <Button variant="outline-primary" size="sm">
            Manage Subscription
          </Button>
        </Link>
      </div>

      {/* Subscription Status */}
      {subscription && (
        <Alert variant={subscription.isActive ? 'success' : 'warning'} className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Current Plan:</strong> {subscription.plan?.displayName || 'Free'}
              {subscription.subscription?.cancelAtPeriodEnd && (
                <Badge bg="warning" className="ms-2">Cancels at period end</Badge>
              )}
            </div>
            {usage && usage.usage && (
              <div className="small">
                Services: {usage.usage?.services?.current || 0}/{usage.usage?.services?.limit === -1 ? '∞' : (usage.usage?.services?.limit || 0)} | 
                Appointments: {usage.usage?.appointments?.current || 0}/{usage.usage?.appointments?.limit === -1 ? '∞' : (usage.usage?.appointments?.limit || 0)}
              </div>
            )}
          </div>
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>Services</Card.Title>
              <Card.Text className="display-4">{dashboardData?.stats.servicesCount || 0}</Card.Text>
              <Link to="/services">
                <Button variant="primary" size="sm">Manage Services</Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>Total Appointments</Card.Title>
              <Card.Text className="display-4">{dashboardData?.stats.totalAppointments || 0}</Card.Text>
              <Link to="/appointments">
                <Button variant="primary" size="sm">View Appointments</Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>Upcoming</Card.Title>
              <Card.Text className="display-4">{dashboardData?.stats.upcomingAppointments || 0}</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>Recent</Card.Title>
              <Card.Text className="display-4">{dashboardData?.stats.recentAppointments || 0}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <Card.Title>Upcoming Appointments</Card.Title>
            </Card.Header>
            <Card.Body>
              {dashboardData?.upcomingAppointments?.length > 0 ? (
                <ListGroup variant="flush">
                  {dashboardData.upcomingAppointments.map(appointment => (
                    <ListGroup.Item key={appointment.id}>
                      <div className="d-flex justify-content-between">
                        <div>
                          <strong>{appointment.customerName}</strong> - {appointment.service?.name || 'Service'}
                          <div className="text-muted">
                            {new Date(appointment.startTime).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <Button variant="outline-primary" size="sm">
                            View
                          </Button>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <p>No upcoming appointments</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <Card.Title>Recent Appointments</Card.Title>
            </Card.Header>
            <Card.Body>
              {dashboardData?.recentAppointments?.length > 0 ? (
                <ListGroup variant="flush">
                  {dashboardData.recentAppointments.map(appointment => (
                    <ListGroup.Item key={appointment.id}>
                      <div className="d-flex justify-content-between">
                        <div>
                          <strong>{appointment.customerName}</strong> - {appointment.service?.name || 'Service'}
                          <div className="text-muted">
                            {new Date(appointment.startTime).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <span className={`badge bg-${appointment.status === 'completed' ? 'success' : 'secondary'}`}>
                            {appointment.status}
                          </span>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <p>No recent appointments</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="mt-4">
        <Button variant="danger" onClick={logout}>
          Logout
        </Button>
      </div>
    </div>
  );
};

export default BusinessDashboard;
